import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, Send, MessageCircle, Phone, X, Check, Copy } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

const InvoiceScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getConfig } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [recipientPhone, setRecipientPhone] = useState('');
  const [copied, setCopied] = useState(false);

  const STORE_WHATSAPP = '9655506070';

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/orders/${id}`, getConfig());
        setOrder(data);
        
        // Auto-detect customer phone if available
        const detectedPhone = 
          data.customerPhone ||
          data.shippingAddress?.phone || 
          data.user?.addresses?.[0]?.phone || 
          data.user?.phone || 
          '';
        setRecipientPhone(detectedPhone.replace(/\D/g, ''));
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching order details:', error);
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id, getConfig]);

  const handlePrint = () => {
    window.print();
  };

  const generateWhatsAppMessage = () => {
    if (!order) return '';

    const orderId = order._id ? order._id.substring(0, 10).toUpperCase() : '';
    const dateStr = new Date(order.createdAt).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const customerName = order.user?.name || 'Customer';
    const address = `${order.shippingAddress?.address || ''}, ${order.shippingAddress?.city || ''} ${order.shippingAddress?.postalCode || ''}`.trim();

    const itemsText = (order.orderItems || [])
      .map(
        (item, idx) =>
          `${idx + 1}. *${item.name}*\n   Qty: ${item.qty} × ₹${item.price.toFixed(2)} = *₹${(item.qty * item.price).toFixed(2)}*`
      )
      .join('\n\n');

    const discountText =
      order.discountPrice > 0
        ? `\n*Discount:* -₹${order.discountPrice.toFixed(2)}`
        : '';

    const invoiceUrl = `${window.location.origin}/orders/${order._id}/invoice`;

    return `🌿 *ALAIAATHI - Homemade Soaps & Products*
📞 *Contact:* +91 ${STORE_WHATSAPP} | ✉️ support@alaiaathi.com
━━━━━━━━━━━━━━━━━━━━━━━━
🧾 *TAX INVOICE / BILL RECEIPT*
*Receipt No:* #INV-${orderId}
*Date:* ${dateStr}
*Customer:* ${customerName}
*Status:* ${order.isPaid ? 'PAID ✅' : 'PENDING ⏳'}
━━━━━━━━━━━━━━━━━━━━━━━━
🛍️ *ORDER ITEMS:*

${itemsText}

━━━━━━━━━━━━━━━━━━━━━━━━
*Subtotal:* ₹${(order.itemsPrice || 0).toFixed(2)}
*Tax:* ₹${(order.taxPrice || 0).toFixed(2)}${discountText}
*Shipping:* ₹${(order.shippingPrice || 0).toFixed(2)}
💰 *TOTAL AMOUNT:* *₹${(order.totalPrice || 0).toFixed(2)}*
━━━━━━━━━━━━━━━━━━━━━━━━
📦 *Delivery Address:*
${address || 'Standard Delivery'}

✨ *Thank you for shopping with Alaiaathi!*
For questions or reorders, message us at +91 ${STORE_WHATSAPP}.

🔗 *Online Bill:*
${invoiceUrl}`;
  };

  const handleSendWhatsApp = (e) => {
    if (e) e.preventDefault();
    let phone = recipientPhone.replace(/\D/g, '');
    
    if (!phone) {
      alert('Please enter the customer WhatsApp phone number.');
      return;
    }

    // Format for India by default if 10 digits
    if (phone.length === 10) {
      phone = `91${phone}`;
    } else if (phone.startsWith('0') && phone.length === 11) {
      phone = `91${phone.substring(1)}`;
    }

    const message = generateWhatsAppMessage();
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phone}&text=${encodedMessage}`;

    window.open(whatsappUrl, '_blank');
    setShowWhatsAppModal(false);
  };

  const handleCopyMessage = () => {
    const message = generateWhatsAppMessage();
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  if (loading) return <div className="p-20 text-center font-bold text-primary-600">Loading invoice...</div>;
  if (!order) return <div className="p-20 text-center font-bold text-red-500">Order not found</div>;

  return (
    <div className="min-h-screen bg-secondary-900 py-10 print:bg-white print:py-0 font-mono text-sm">
      <style>
        {`
          @media print {
            @page {
              margin: 0;
            }
            body {
              margin: 0;
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>

      {/* Action Bar (Hidden during print) */}
      <div className="max-w-md mx-auto mb-6 px-4 flex flex-wrap gap-3 justify-between items-center print:hidden text-white">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-secondary-400 hover:text-white font-bold transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </button>

        <div className="flex items-center space-x-2">
          {/* WhatsApp Send Button */}
          <button
            onClick={() => setShowWhatsAppModal(true)}
            className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 rounded-xl font-bold flex items-center space-x-2 transition-all shadow-lg shadow-emerald-900/40 text-xs sm:text-sm"
          >
            <MessageCircle className="h-4 w-4" />
            <span>Send WhatsApp</span>
          </button>

          {/* Print Button */}
          <button
            onClick={handlePrint}
            className="bg-primary-600 hover:bg-primary-500 text-white px-4 py-2.5 rounded-xl font-bold flex items-center space-x-2 transition-all shadow-lg text-xs sm:text-sm"
          >
            <Printer className="h-4 w-4" />
            <span>Print</span>
          </button>
        </div>
      </div>

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm print:hidden">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full text-secondary-900 shadow-2xl relative animate-in fade-in zoom-in duration-200">
            <button
              onClick={() => setShowWhatsAppModal(false)}
              className="absolute top-5 right-5 text-secondary-400 hover:text-secondary-700 transition-colors p-1"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-emerald-100 p-3 rounded-2xl text-emerald-600">
                <MessageCircle className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-black tracking-tight">Send Bill via WhatsApp</h3>
                <p className="text-xs text-secondary-500 font-sans">
                  Sender: <strong className="text-emerald-700">+91 {STORE_WHATSAPP}</strong>
                </p>
              </div>
            </div>

            <form onSubmit={handleSendWhatsApp} className="space-y-4 font-sans">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-secondary-600 mb-1.5">
                  Customer WhatsApp Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-secondary-400">
                    <Phone className="h-4 w-4" />
                  </div>
                  <input
                    type="tel"
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(e.target.value)}
                    placeholder="e.g. 9876543210 or 919876543210"
                    required
                    className="w-full pl-10 pr-4 py-3 bg-secondary-50 border border-secondary-200 rounded-2xl text-sm font-bold text-secondary-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <p className="text-[11px] text-secondary-400 mt-1">
                  Bill details and breakdown will be formatted cleanly into WhatsApp.
                </p>
              </div>

              {/* Message preview snippet */}
              <div className="p-3 bg-secondary-50 rounded-2xl border border-secondary-100 text-[11px] text-secondary-600 max-h-32 overflow-y-auto font-mono whitespace-pre-wrap">
                {generateWhatsAppMessage()}
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleCopyMessage}
                  className="flex-1 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 font-bold py-3 rounded-xl flex items-center justify-center space-x-1.5 text-xs transition-colors"
                >
                  {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  <span>{copied ? 'Copied!' : 'Copy Bill'}</span>
                </button>

                <button
                  type="submit"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-xl flex items-center justify-center space-x-1.5 text-xs transition-colors shadow-lg shadow-emerald-200"
                >
                  <Send className="h-4 w-4" />
                  <span>Send Now</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Thermal Receipt Container */}
      <div className="max-w-[320px] mx-auto bg-white p-6 text-black print:max-w-full print:w-full print:px-2 print:py-4 print:m-0 print:shadow-none shadow-2xl relative">
        {/* Jagged Edge (Top) - Hidden in print */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-secondary-900 print:hidden" style={{ clipPath: 'polygon(0% 0%, 5% 100%, 10% 0%, 15% 100%, 20% 0%, 25% 100%, 30% 0%, 35% 100%, 40% 0%, 45% 100%, 50% 0%, 55% 100%, 60% 0%, 65% 100%, 70% 0%, 75% 100%, 80% 0%, 85% 100%, 90% 0%, 95% 100%, 100% 0%)' }}></div>
        
        <div className="pt-4 pb-8 space-y-6">
          
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-black uppercase tracking-widest text-black">ALAIAATHI</h1>
            <p className="text-xs leading-tight font-bold print:text-black">
              Homemade Soaps and Products<br />
              Palladam, Tamil Nadu, India<br />
              Ph/WhatsApp: +91 {STORE_WHATSAPP}<br />
              support@alaiaathi.com
            </p>
          </div>

          <div className="border-t border-dashed border-gray-400 print:border-black"></div>

          {/* Invoice & Customer Info (Side by Side) */}
          <div className="text-xs space-y-1.5 font-bold print:text-black">
            <div className="flex justify-between items-baseline">
              <span>RECEIPT NO:</span>
              <span className="text-right font-black">#INV-{order._id.substring(0, 8).toUpperCase()}</span>
            </div>
            <div className="flex justify-between items-baseline">
              <span>DATE:</span>
              <span className="text-right">{new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <div className="flex justify-between items-baseline pt-1 border-t border-dotted border-gray-300 print:border-black">
              <span>CUSTOMER:</span>
              <span className="text-right font-black">{order.customerName || order.user?.name || 'Walk-in Customer'}</span>
            </div>
            {(order.customerPhone || order.shippingAddress?.phone || order.user?.addresses?.[0]?.phone) && (
              <div className="flex justify-between items-baseline">
                <span>PHONE:</span>
                <span className="text-right">{order.customerPhone || order.shippingAddress?.phone || order.user?.addresses?.[0]?.phone}</span>
              </div>
            )}
            {(order.customerEmail || (!order.isManualBill && order.user?.email)) && (
              <div className="flex justify-between items-baseline">
                <span>EMAIL:</span>
                <span className="text-right truncate max-w-[170px]">{order.customerEmail || order.user?.email}</span>
              </div>
            )}
          </div>

          <div className="border-t border-dashed border-gray-400 print:border-black"></div>

          {/* Items */}
          <div className="space-y-3 text-xs">
            <div className="flex justify-between font-black uppercase pb-1 border-b border-black print:border-black">
              <span>Item</span>
              <span>Total</span>
            </div>
            
            {order.orderItems?.map((item, idx) => (
              <div key={idx} className="space-y-1 print:text-black">
                <div className="font-bold">{item.name}</div>
                <div className="flex justify-between text-gray-700 print:text-black font-bold">
                  <span>{item.qty} x ₹{item.price.toFixed(2)}</span>
                  <span className="font-black text-black">₹{(item.qty * item.price).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-dashed border-gray-400 print:border-black"></div>

          {/* Totals */}
          <div className="space-y-1 text-xs font-bold print:text-black">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>₹{(order.itemsPrice || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Tax:</span>
              <span>₹{(order.taxPrice || 0).toFixed(2)}</span>
            </div>
            {order.discountPrice > 0 && (
              <div className="flex justify-between">
                <span>Discount:</span>
                <span>-₹{order.discountPrice.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Shipping:</span>
              <span>₹{(order.shippingPrice || 0).toFixed(2)}</span>
            </div>
          </div>

          <div className="border-t border-black print:border-black"></div>
          
          <div className="flex justify-between font-black text-sm uppercase print:text-black">
            <span>Total:</span>
            <span>₹{(order.totalPrice || 0).toFixed(2)}</span>
          </div>

          <div className="border-t border-dashed border-gray-400 print:border-black"></div>

          {/* Payment Info */}
          <div className="text-xs space-y-1 font-bold print:text-black">
            <div className="flex justify-between">
              <span>Payment Mode:</span>
              <span>{order.paymentMethod || 'Cash'}</span>
            </div>
            <div className="flex justify-between">
              <span>Status:</span>
              <span className={order.isPaid ? 'text-emerald-700 print:text-black' : 'text-red-600 print:text-black'}>
                {order.isPaid ? 'PAID ✅' : 'PENDING ⏳'}
              </span>
            </div>
          </div>

          {/* Customer Address / Billing Location (Separately at the end of the bill) */}
          {order.shippingAddress?.address && (
            <>
              <div className="border-t border-dashed border-gray-400 print:border-black"></div>
              <div className="text-xs space-y-1 print:text-black">
                <p className="font-black uppercase tracking-wider text-[11px]">
                  {order.isManualBill ? 'Billing Location:' : 'Delivery Address:'}
                </p>
                <div className="text-gray-800 print:text-black font-bold leading-tight">
                  <p>{order.shippingAddress.address}</p>
                  {(order.shippingAddress.city || order.shippingAddress.postalCode) && (
                    <p>{order.shippingAddress.city}{order.shippingAddress.postalCode ? `, ${order.shippingAddress.postalCode}` : ''}</p>
                  )}
                  {order.shippingAddress.country && <p>{order.shippingAddress.country}</p>}
                </div>
              </div>
            </>
          )}

          <div className="border-t border-dashed border-gray-400 print:border-black"></div>

          {/* Footer */}
          <div className="text-center text-xs space-y-2 print:text-black">
            <p className="font-black uppercase tracking-widest">Thank You!</p>
            <p className="text-[10px] font-bold">Please retain this receipt for your records.</p>
            
            {/* Barcode Mockup */}
            <div className="pt-4 flex justify-center">
              <div className="h-10 w-48 bg-[repeating-linear-gradient(to_right,black,black_2px,white_2px,white_4px,black_4px,black_5px,white_5px,white_8px,black_8px,black_12px,white_12px,white_14px)]"></div>
            </div>
            <p className="text-[8px] tracking-widest mt-1 font-bold">{order._id.toUpperCase()}</p>
          </div>

        </div>

        {/* Jagged Edge (Bottom) - Hidden in print */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-secondary-900 print:hidden" style={{ clipPath: 'polygon(0% 100%, 5% 0%, 10% 100%, 15% 0%, 20% 100%, 25% 0%, 30% 100%, 35% 0%, 40% 100%, 45% 0%, 50% 100%, 55% 0%, 60% 100%, 65% 0%, 70% 100%, 75% 0%, 80% 100%, 85% 0%, 90% 100%, 95% 0%, 100% 100%)' }}></div>
      </div>
    </div>
  );
};

export default InvoiceScreen;
